import {
  Button,
  DateUtils,
  Image,
  Markdown,
  Picture,
  TagUpdated,
  Typography,
  Video,
} from '@vk-tech/mark-ui';
import classNames from 'classnames';
import { MouseEvent } from 'react';

import { Popup } from '~components/Popup/Popup';
import { CopyBlock } from '~containers/PopupResolver/PopupCard/CopyBlock/CopyBlock';
import { useUiActions } from '~hooks/useUiActions';
import { DatasetActionValues } from '~types/Dataset.types';

import styles from './PopupCard.module.scss';
import { PopupCardCommonProps } from './PopupCard.types';

export function PopupCard({
  isVisibleButtonClose,
  logoFirst,
  logoSecond,
  labels,
  date,
  location,
  heading,
  text,
  promocode,
  buttons,
  disclaimer,
  image,
  video,
  isReversed = false,
  imageSettings,
  cardSize = 'm',
  promocodeApiActions,
  scrollLock,
  mod = 'dark',
  popupMod = 'center',
  isOverlayClose,
  opaque = false,
  onClose,
  onTargetAction,
  active,
}: PopupCardCommonProps) {
  const { handleAction } = useUiActions();
  const detailedDate = date
    ? DateUtils.getDetailedDateTime(date, { timeZone: 'Europe/Moscow' })
    : undefined;
  const formattedDate = detailedDate
    ? `${detailedDate.day} ${detailedDate.month} ${detailedDate.year}, ${detailedDate.time}`
    : '';
  const locationDateText =
    formattedDate && location
      ? `${formattedDate} / ${location}`
      : formattedDate || location;
  const hasLabels = !!labels?.length;
  const hasLocationDate = !!(location || date);
  const isSmall = cardSize === 's';

  return (
    <Popup
      active={active}
      isVisibleButtonClose={isVisibleButtonClose}
      onClose={onClose}
      scrollLock={scrollLock}
      popupMod={popupMod}
      mod={mod}
      isOverlayClose={isOverlayClose}
      opaque={opaque}
    >
      <div
        className={classNames(
          styles.body,
          isReversed && styles.reversed,
          styles[cardSize],
        )}
      >
        <div className={styles.content}>
          <div className={styles.texts}>
            {(logoFirst?.src || logoSecond?.src) && (
              <div className={styles.logo}>
                {logoFirst && (
                  <Image className={styles.logoImage} {...logoFirst} />
                )}
                {logoSecond && (
                  <span className={styles.divider} aria-hidden="true" />
                )}
                {logoSecond && (
                  <Image className={styles.logoImage} {...logoSecond} />
                )}
              </div>
            )}
            {hasLabels && (
              <div className={styles.labels}>
                {labels!.map((label) => (
                  <TagUpdated {...label} key={label.id} />
                ))}
                {isSmall && hasLocationDate && (
                  <Typography mod="captionM" tagName="div">
                    {locationDateText}
                  </Typography>
                )}
              </div>
            )}
            {hasLocationDate && (!isSmall || !hasLabels) && (
              <Typography
                mod="captionM"
                tagName="span"
                className={styles.locationDate}
              >
                {locationDateText}
              </Typography>
            )}
            <Typography mod="titleXl" tagName="h2">
              {heading}
            </Typography>
            {text && (
              <Markdown className={styles.text} typographyMod="bodyS">
                {text}
              </Markdown>
            )}
            {promocode && (
              <CopyBlock
                text={promocode}
                promocodeApiActions={promocodeApiActions}
                onTargetAction={onTargetAction}
                className={styles.promocode}
              />
            )}
          </div>
          {(buttons?.length || disclaimer) && (
            <div className={styles.popupFooter}>
              {buttons?.length && (
                <div className={styles.buttons}>
                  {buttons.map(({ apiActions, ...button }) => {
                    const filteredApiActions = Object.fromEntries(
                      Object.entries(apiActions || {}).filter(
                        ([name]) => name !== 'target-action',
                      ),
                    );

                    return (
                      <Button
                        {...button}
                        apiActions={filteredApiActions}
                        size="s"
                        key={button.id}
                        onClick={(e: MouseEvent<HTMLButtonElement>) => {
                          const targetAction = apiActions?.['target-action'];
                          if (targetAction && onTargetAction)
                            onTargetAction(targetAction, true, {
                              ymEvent: apiActions?.['data-ui-ym-event'],
                              tmrEvent: apiActions?.['data-ui-tmr-event'],
                            });
                          const uiAction = apiActions?.[
                            'data-ui-action'
                          ] as DatasetActionValues;
                          if (uiAction) handleAction(uiAction, e.currentTarget);
                          onClose?.();
                          button.onClick?.(e);
                        }}
                      />
                    );
                  })}
                </div>
              )}

              {disclaimer && (
                <Typography mod="captionM" className={styles.disclaimer}>
                  {disclaimer}
                </Typography>
              )}
            </div>
          )}
        </div>
        <div className={styles.media}>
          {image && (
            <Picture
              {...image}
              className={classNames(
                styles.image,
                imageSettings && styles.maxWidth,
                imageSettings &&
                  imageSettings.position &&
                  styles[imageSettings.position],
                imageSettings &&
                  imageSettings.objectFit &&
                  styles[imageSettings.objectFit],
              )}
            />
          )}
          {video && (
            <Video
              autoPlay
              loop
              muted
              playsInline
              poster={image ? image.src : ''}
              {...video}
              className={classNames(
                styles.video,
                imageSettings && styles.maxWidth,
                imageSettings &&
                  imageSettings.position &&
                  styles[imageSettings.position],
                imageSettings &&
                  imageSettings.objectFit &&
                  styles[imageSettings.objectFit],
              )}
            />
          )}
        </div>
      </div>
    </Popup>
  );
}
